import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as solidIcons from '@fortawesome/free-solid-svg-icons';
import { useBusinessData } from '../../context/BusinessContext';
import '../../css/Services.css';

// Icon mapping function to get the right FontAwesome icon from the string in JSON
const iconMapping = (iconName) => {
  return iconName && solidIcons[iconName] ? solidIcons[iconName] : solidIcons.faChartLine;
};

const Services = () => {
  const { loading, error, getData, hasData, getImageUrl } = useBusinessData();

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (error) {
    return <div className="error">Error loading data: {error}</div>;
  }

  const services = getData('services', []);
  const process = getData('process', []);
  
  // Get services hero background image from JSON using getImageUrl
  const servicesHeroImage = getImageUrl('images.backgrounds.services', 'services-hero.jpg');
  
  // Get CTA background image from JSON using getImageUrl
  const ctaImage = getImageUrl('images.backgrounds.cta', 'cta-bg.jpg');
  
  // Create inline styles for background images - using URLs directly
  const servicesHeroStyle = {
    backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url('${servicesHeroImage}')`
  };
  
  const ctaStyle = {
    backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.8)), url('${ctaImage}')`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundAttachment: 'fixed'
  };

  return (
    <>
      {/* Services Hero */}
      <div className="services-hero" style={servicesHeroStyle}>
        <div className="container">
          <div className="services-hero-content">
            <h1>{getData('ui.services.pageTitle', 'Our Services')}</h1>
            <p>{getData('ui.services.pageSubtitle', 'Comprehensive business solutions tailored to your needs')}</p>
          </div>
        </div>
      </div>
      
      {/* Services Intro */}
      <section className="section">
        <div className="container">
          <h2 className="section-title">{getData('ui.services.mainSection.title', 'What We Offer')}</h2>
          <p className="section-subtitle">
            {getData('ui.services.mainSection.subtitle', 'We provide a wide range of business services designed to help your organization grow, optimize operations, and achieve sustainable success in today\'s competitive market.')}
          </p>
          
          <div className="services-grid">
            {services.map((service, index) => (
              <div className="service-card" key={service.id || index}>
                <div className="service-icon">
                  <FontAwesomeIcon icon={iconMapping(service.icon)} />
                </div>
                <h3>{service.title}</h3>
                <p>{service.description}</p>
                <ul className="service-features">
                  {service.features.map((feature, idx) => (
                    <li key={idx}>{feature}</li>
                  ))}
                </ul>
                <div className="service-buttons">
                  {service.learnMoreLink && (
                    <Link to={service.learnMoreLink} className="btn-service">
                      {getData('ui.global.learnMoreButton', 'Learn More')}
                    </Link>
                  )}
                  {service.viewServiceLink && (
                    <Link to={service.viewServiceLink} className="btn-service-secondary">
                      {getData('ui.global.viewDetailsButton', 'View Details')} <FontAwesomeIcon icon={solidIcons.faArrowRight} />
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Process Section */}
      <section className="section section-dark">
        <div className="container">
          <h2 className="section-title">{getData('ui.services.processSection.title', 'Our Process')}</h2>
          <p className="section-subtitle">{getData('ui.services.processSection.subtitle', 'How we work with you to deliver exceptional results')}</p>
          
          <div className="process-container">
            {process.map((step) => (
              <div className="process-step" key={step.step}>
                <div className="step-number">{step.step}</div>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="section cta-section" style={ctaStyle}>
        <div className="container">
          <div className="cta-content">
            <h2>{getData('ui.services.cta.title', 'Ready to Transform Your Business?')}</h2>
            <p>{getData('ui.services.cta.subtitle', 'Contact us today to discuss how our services can help you achieve your business goals.')}</p>
            <Link to="/contact" className="btn btn-primary">{getData('ui.global.contactUsButton', 'Contact Us')}</Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default Services; 